/*
 * Description:
 * ---------------------
 * A box's focused view, opened from the master map: one source box at a time,
 * read left to right as a sentence. Devices at the source (left, a solid card) connect using an
 * address (on the path) to a device over there (right, under the box it is
 * at). Every path ends at its device: a curve for a few, and for more, one
 * trunk out of the source branching to each. Its colour repeats its state,
 * the accent while applied, grey while being set up or paused, red when
 * failed.
 * Destinations group by their box, and a group folds; past FIND of them a
 * search narrows them. Picking a device highlights its path and opens its
 * details in place: the address to copy, its setup, the traffic its two boxes
 * observed, the address check for an address inside the source's own
 * network, and what can be done. The source and the boxes it reaches are
 * asked for /reach every 15 s. Back to master map returns to the map as it
 * was. The details are RuleDetails; wording lives in reachWords.js.
 *
 * ! file components/AccessCanvas.js
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiArrowLeft, FiChevronDown, FiChevronRight, FiGrid, FiPlus, FiSearch } from "react-icons/fi";
import { TbRouter } from "react-icons/tb";
import useBoxReads from "../hooks/useBoxReads";
import { DeviceIcon, getDeviceType } from "./NetworkCanvas";
import RuleDetails from "./RuleDetails";
import * as W from "./reachWords";

const POLL_MS = 15000;
// Past FIND destinations, a search appears.
const FIND = 6;
// Up to TABS boxes, the source is picked from tabs; past it, from a list.
const TABS = 5;
// The boxes asked for what they counted, at most.
const OWNERS = 12;
// A folded group's path takes the worst state among its devices.
const WORST = ["bad", "wait", "good", "off"];
// Past TRUNK paths, one trunk branches to each instead of a curve apiece.
const TRUNK = 4;

export default function AccessCanvas({
  group, sites, entries, view, source, onSource, selected, onSelect, canWrite, busy,
  onBack, onAllow, onManage, onToggle, onRemoveAccess,
}) {
  const anchors = view.anchors;
  const byId = useMemo(() => Object.fromEntries(sites.map((s) => [s.node_id, s])), [sites]);
  const site = byId[source];
  const rules = useMemo(() => W.rulesOf(entries).filter((r) => r.box === source)
    .sort((a, b) => a.entry.site.localeCompare(b.entry.site) || a.entry.name.localeCompare(b.entry.name)), [entries, source]);
  const owners = useMemo(() => [...new Set(rules.map((r) => r.entry.site))], [rules]);
  const { reads, ask } = useBoxReads([source, ...owners.slice(0, OWNERS)], "/reach", POLL_MS);
  const [query, setQuery] = useState("");
  const [folded, setFolded] = useState({});
  const q = query.trim().toLowerCase();
  const shown = q ? rules.filter((r) => [r.entry.name, r.entry.lan_address, r.address, r.entry.site].some((v) => v.toLowerCase().includes(q))) : rules;
  const toneOf = (r) => {
    const setup = W.setupOf(r.entry, r.box, anchors);
    return W.pathTone(setup, setup.state === "applied" ? W.addressCheckOf(r.entry, r.box, r.address, site, reads[source]) : null);
  };
  const paths = useRef(new Map());
  const end = (key) => (el) => {
    if (el) paths.current.set(key, el);
    else paths.current.delete(key);
  };
  const { canvas, from, geo } = usePaths(paths);
  const lines = {};
  const groups = [...new Set(shown.map((r) => r.entry.site))].map((owner) => {
    const mine = shown.filter((r) => r.entry.site === owner);
    const isFolded = !q && folded[owner] && !mine.some((r) => r.key === selected);
    const tones = mine.map((r) => ({ r, tone: toneOf(r) }));
    if (isFolded) lines[`site:${owner}`] = { tone: WORST.find((t) => tones.some((x) => x.tone === t)), dim: !!selected };
    else for (const { r, tone } of tones) lines[r.key] = { tone, on: r.key === selected, dim: !!selected && r.key !== selected };
    return { owner, tones, isFolded };
  });
  const fold = (owner) => setFolded((f) => ({ ...f, [owner]: !f[owner] }));
  const drawn = geo ? route(geo, lines) : null;
  const state = site ? W.readiness(site) : "ready";

  return (
    <section className="panel rd-ac" aria-label={`Access from ${source}`}>
      <header className="rd-ac-head">
        <div className="rd-ac-lead">
          <button type="button" className="dl-btn rd-small" onClick={onBack}><FiArrowLeft size={14} /> {W.BACK}</button>
          <SourcePicker sites={sites} source={source} onSource={onSource} />
        </div>
        <div className="rd-ac-tools">
          {rules.length > FIND && (
            <label className="rd-search">
              <FiSearch size={13} aria-hidden="true" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={W.FIND} aria-label={W.FIND} />
            </label>
          )}
          {canWrite && sites.length > 1 && (
            <button type="button" className="dl-btn primary" disabled={busy} onClick={onAllow}>
              <FiPlus size={14} /> {W.ALLOW_ACCESS}
            </button>
          )}
        </div>
      </header>
      <div className="rd-ac-canvas" ref={canvas}>
        {drawn && (
          <svg className="rd-ac-paths" width={geo.w} height={geo.h} aria-hidden="true">
            {drawn.trunk && <path d={drawn.trunk} className={`rd-ac-path rd-ac-trunk${selected ? " is-dim" : ""}`} />}
            {drawn.paths.map(({ key, d, line }) => (
              <path key={key} d={d} className={`rd-ac-path rd-${line.tone}${line.on ? " is-on" : ""}${line.dim ? " is-dim" : ""}`} />
            ))}
          </svg>
        )}
        <div className={`rd-ac-source is-${state}`} ref={from}>
          <TbRouter size={22} aria-hidden="true" />
          <strong>{source}</strong>
          <span>{W.SOURCE_CAPTION}</span>
          <span className="rd-mono">{W.lanOf(site) || "—"}</span>
          {state !== "ready" && <em>{W.READINESS[state]}</em>}
        </div>
        <div className="rd-ac-dests">
          {sites.length === 1 && <p className="rd-ac-empty">{W.onlyBox(source, group.name)}</p>}
          {sites.length > 1 && state === "update" && <p className="rd-ac-empty">{W.SITE_UPDATE}</p>}
          {sites.length > 1 && state !== "update" && !rules.length && <p className="rd-ac-empty">{W.noAccess(source)}</p>}
          {rules.length > 0 && !shown.length && <p className="rd-ac-empty">{W.NO_MATCH}</p>}
          {groups.map(({ owner, tones, isFolded }) => (
            <section key={owner} className="rd-ac-group" aria-label={`At ${owner}`}>
              <button type="button" className="rd-ac-site" aria-expanded={!isFolded} onClick={() => fold(owner)}>
                <span className="rd-ac-link" ref={isFolded ? end(`site:${owner}`) : undefined}>
                  {isFolded && <span className={`rd-ac-tail rd-${lines[`site:${owner}`].tone}`} aria-hidden="true" />}
                </span>
                <span className="rd-ac-site-name">
                  {isFolded ? <FiChevronRight size={14} aria-hidden="true" /> : <FiChevronDown size={14} aria-hidden="true" />}
                  <TbRouter size={15} aria-hidden="true" />
                  <b>{owner}</b>
                  <span className="rd-mono">{W.lanOf(byId[owner])}</span>
                  {W.readiness(byId[owner]) !== "ready" && <em>{W.READINESS[W.readiness(byId[owner])]}</em>}
                  {isFolded && <em>{W.deviceCount(tones.length)}</em>}
                </span>
              </button>
              {!isFolded && (
                <ul>
                  {tones.map(({ r, tone }) => (
                    <Destination key={r.key} rule={r} tone={tone} line={lines[r.key]} end={end(r.key)}
                      onPick={() => onSelect(r.key === selected ? null : r.key)}>
                      {r.key === selected && (
                        <div className="rd-ac-details">
                          <RuleDetails rule={r} site={site} mine={reads[source]} theirs={reads[owner]} anchors={anchors}
                            canWrite={canWrite} busy={busy} onRefresh={() => ask([source, owner])}
                            onManage={() => onManage(r.entry)} onToggle={() => onToggle(r.entry)} onRemove={() => onRemoveAccess(r)} />
                        </div>
                      )}
                    </Destination>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
    </section>
  );
}

/* The box whose devices connect: tabs for a few boxes, a list for more. */
function SourcePicker({ sites, source, onSource }) {
  const note = (s) => (W.readiness(s) === "ready" ? "" : W.READINESS[W.readiness(s)]);
  if (sites.length > TABS) {
    return (
      <label className="rd-from">
        <span>{W.FROM_LABEL}</span>
        <select className="dl-input" value={source} onChange={(e) => onSource(e.target.value)} aria-label={W.FROM_LABEL}>
          {sites.map((s) => <option key={s.node_id} value={s.node_id}>{s.node_id}{note(s) ? ` (${note(s)})` : ""}</option>)}
        </select>
      </label>
    );
  }
  return (
    <div className="rd-from" role="group" aria-label={W.FROM_LABEL}>
      <span>{W.FROM_LABEL}</span>
      <div className="rd-tabs">
        {sites.map((s) => (
          <button key={s.node_id} type="button" className={`rd-tab${s.node_id === source ? " is-on" : ""}`}
            aria-pressed={s.node_id === source} onClick={() => onSource(s.node_id)}>
            {s.node_id}{note(s) && <em>{note(s)}</em>}
          </button>
        ))}
      </div>
    </div>
  );
}

/* One device the source may connect to: the address on its path, then the
   device, named, with its own address under it. */
function Destination({ rule, tone, line, end, onPick, children }) {
  const e = rule.entry;
  const range = W.isRange(e.lan_address);
  return (
    <li className={`rd-ac-item${line.on ? " is-on" : ""}${line.dim ? " is-dim" : ""}`} aria-label={e.name}>
      <button type="button" className="rd-ac-row" aria-expanded={line.on} onClick={onPick}
        aria-label={W.pathLabel(rule.box, e.name, e.site, rule.address)}>
        <span className="rd-ac-link" ref={end}>
          <span className={`rd-ac-pill rd-mono rd-${tone}`}>{rule.address}</span>
          <span className={`rd-ac-tail rd-${tone}`} aria-hidden="true" />
        </span>
        <span className="rd-ac-card">
          <span className="rd-ac-icon" aria-hidden="true">
            {range ? <FiGrid size={18} /> : <DeviceIcon type={getDeviceType({ hostname: e.name })} size={18} />}
          </span>
          <span className="rd-ac-name">
            <strong>{e.name}</strong>
            <span>{range ? W.everyDevice(e.lan_address) : <>{W.LOCAL_IP} <span className="rd-mono">{e.lan_address}</span></>}</span>
          </span>
          {tone !== "good" && <em className={`rd-ac-tag rd-${tone}`}>{W.TONE_WORDS[tone]}</em>}
        </span>
      </button>
      {children}
    </li>
  );
}

/* The paths to draw from the measured ends: a curve apiece for up to TRUNK,
   and past it a trunk out of the source with a branch to each end, rounded
   where it leaves the trunk. The path picked runs whole, drawn last. */
function route(geo, lines) {
  const { x, y } = geo;
  const ends = geo.ends.filter((e) => lines[e.key]);
  const last = (a, b) => Number(lines[a.key].on) - Number(lines[b.key].on);
  if (ends.length <= TRUNK) {
    return {
      paths: ends.sort(last).map((e) => {
        const dx = Math.max(24, (e.x - x) / 2);
        return { key: e.key, line: lines[e.key], d: `M ${x} ${y} C ${x + dx} ${y}, ${e.x - dx} ${e.y}, ${e.x} ${e.y}` };
      }),
    };
  }
  const xs = x + Math.min(36, (Math.min(...ends.map((e) => e.x)) - x) / 3);
  const bend = (e) => Math.min(10, Math.abs(e.y - y));
  const turn = (e) => `${xs} ${e.y - Math.sign(e.y - y) * bend(e)} Q ${xs} ${e.y} ${xs + bend(e)} ${e.y} H ${e.x}`;
  const reach = (side) => ends.filter((e) => side * (e.y - y) > 0).reduce((far, e) => Math.max(far, side * (e.y - y) - bend(e)), 0);
  return {
    trunk: `M ${x} ${y} H ${xs} M ${xs} ${y - reach(-1)} V ${y + reach(1)}`,
    paths: ends.sort(last).map((e) => {
      const line = lines[e.key];
      if (bend(e) < 1) return { key: e.key, line, d: `M ${line.on ? x : xs} ${e.y} H ${e.x}` };
      return { key: e.key, line, d: line.on ? `M ${x} ${y} H ${xs} L ${turn(e)}` : `M ${turn(e)}` };
    }),
  };
}

/* usePaths measures where the paths run: from the right edge of the source
   card to the start of each destination's link, in the canvas's own
   coordinates, after every render and whenever the canvas resizes. */
function usePaths(ends) {
  const canvas = useRef(null);
  const from = useRef(null);
  const [geo, setGeo] = useState(null);
  const measure = useCallback(() => {
    const c = canvas.current;
    const s = from.current;
    if (!c || !s) return;
    const box = c.getBoundingClientRect();
    const src = s.getBoundingClientRect();
    const next = {
      w: Math.round(box.width), h: Math.round(box.height),
      x: Math.round(src.right - box.left), y: Math.round(src.top - box.top + src.height / 2),
      ends: [...ends.current].map(([key, el]) => {
        const r = el.getBoundingClientRect();
        return { key, x: Math.round(r.left - box.left), y: Math.round(r.top - box.top + r.height / 2) };
      }),
    };
    setGeo((was) => (JSON.stringify(was) === JSON.stringify(next) ? was : next));
  }, [ends]);
  useLayoutEffect(measure);
  useEffect(() => {
    if (!canvas.current || typeof ResizeObserver === "undefined") return undefined;
    const watch = new ResizeObserver(measure);
    watch.observe(canvas.current);
    return () => watch.disconnect();
  }, [measure]);
  return { canvas, from, geo };
}
